import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import useUpdateCandidateBoard from '../api/useUpdateCandidatesBoard';
import CandidatesBoardColumn from './CandidatesBoardColumn';
import CandidatesBoardLoading from './CandidatesBoardLoading';
import type {
  AgentApiCandidateProfilesBoard,
  AgentApiCandidateProfilesIndex,
  AgentApiCandidateProfilesItem,
  AgentApiCandidateBoardsShow,
} from '@/types/serializers';
import CandidatesShowDrawer from './CandidatesShowDrawer';
import type { Color } from '../types/CandidateBoard';
import usePostCandidateBoard from '../api/usePostCandidateBoard';
import { showErrorToast } from '../../job_change_processes/lib/toast';
import useDeleteCandidateBoard from '../api/useDeleteCandidateBoard';
import usePostCandidateBoardsMove from '../api/usePostCandidateBoardsMove';
import type { BoardPageState } from '../api/useFetchCandidateBoardsDetails';
import { DragDropContext, type DropResult } from '@hello-pangea/dnd';

type Props = {
  boards: AgentApiCandidateProfilesBoard[];
  boardsData: (AgentApiCandidateBoardsShow | undefined)[];
  boardStates: Record<number, BoardPageState>;
  uncategorized?: AgentApiCandidateProfilesIndex;
  boardsRefetch: (options?: { resetPages?: boolean }) => Promise<unknown>;
  reloadBoards: (ids: number[]) => void;
  uncategorizedRefetch: (options?: { nextPage?: boolean; resetPage?: boolean }) => Promise<unknown>;
  onLoadMoreBoard: (boardId: number) => void;
  uncategorizedHasNextPage?: boolean;
  uncategorizedIsFetching?: boolean;
  isLoading?: boolean;
  filterKey?: string;
};

export type CandidatesBoardScreenRef = {
  scrollToEnd: () => void;
};

const CandidatesBoardScreen = forwardRef<CandidatesBoardScreenRef, Props>(
  (
    {
      boards,
      boardsData,
      boardStates,
      uncategorized,
      boardsRefetch,
      reloadBoards,
      uncategorizedRefetch,
      onLoadMoreBoard,
      uncategorizedHasNextPage = false,
      uncategorizedIsFetching = false,
      isLoading = false,
      filterKey = '',
    },
    ref
  ) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
    const [grouped, setGrouped] = useState<Record<string, AgentApiCandidateProfilesItem[]>>({});
    const [counts, setCounts] = useState<Record<string, number>>({});
    const [hasLoaded, setHasLoaded] = useState(false);
    const prevFilterKeyRef = useRef(filterKey);
    const prevSourcesRef = useRef<Record<string, object>>({});

    useImperativeHandle(ref, () => ({
      scrollToEnd: () => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
        }
      },
    }));

    const { mutateAsync: addBoard } = usePostCandidateBoard();
    const { mutateAsync: updateBoard } = useUpdateCandidateBoard();
    const { mutateAsync: deleteBoard } = useDeleteCandidateBoard();
    const { mutateAsync: moveProfile } = usePostCandidateBoardsMove();

    useEffect(() => {
      if (!isLoading) setHasLoaded(true);
    }, [isLoading]);

    useEffect(() => {
      const sources: Record<string, { records: unknown[]; total_count: number }> = {};
      if (uncategorized) sources.uncategorized = uncategorized;
      boardsData.forEach((data) => {
        if (data?.board) sources[`board_${data.board.id}`] = data;
      });

      const filterChanged = prevFilterKeyRef.current !== filterKey;
      prevFilterKeyRef.current = filterKey;
      // 変わった列だけ反映する。全列を上書きすると D&D の楽観更新が古いデータで戻る
      const changedKeys = Object.keys(sources).filter(
        (key) => filterChanged || prevSourcesRef.current[key] !== sources[key]
      );
      prevSourcesRef.current = sources;

      const nextGrouped = Object.fromEntries(
        changedKeys.map((key) => [
          key,
          sources[key].records as unknown as AgentApiCandidateProfilesItem[],
        ])
      );
      const nextCounts = Object.fromEntries(
        changedKeys.map((key) => [key, sources[key].total_count])
      );
      setGrouped((prev) => (filterChanged ? nextGrouped : { ...prev, ...nextGrouped }));
      setCounts((prev) => (filterChanged ? nextCounts : { ...prev, ...nextCounts }));
    }, [boardsData, uncategorized, filterKey]);

    const handleLoadMoreUncategorized = async () => {
      if (!uncategorizedHasNextPage || uncategorizedIsFetching) return;
      await uncategorizedRefetch({ nextPage: true });
    };

    const handleLoadMore = async (boardId: number | string) => {
      if (boardId === 'uncategorized') {
        await handleLoadMoreUncategorized();
        return;
      }
      onLoadMoreBoard(Number(boardId));
    };

    const handleCreateBoard = async () => {
      try {
        await addBoard({
          title: 'Newボード',
          divide_status: '',
          color_type: 'gray',
          order_number: boards.length,
        });
        await boardsRefetch();
      } catch (error) {
        showErrorToast('ボードの作成に失敗しました');
      }
    };

    const handleDeleteBoard = async (id: number) => {
      try {
        await deleteBoard({
          targetBoardId: id,
        });
        await boardsRefetch();
        await uncategorizedRefetch({ resetPage: true });
      } catch (error) {
        showErrorToast('ボードの削除に失敗しました');
      }
    };

    const handleUpdateBoardTitle = async (boardId: number, newTitle: string) => {
      try {
        const board = boards.find((b) => b.id === boardId);
        if (!board) return;

        await updateBoard({
          id: boardId,
          title: newTitle,
          divide_status: board.divide_status,
          order_number: board.order_number,
          color_type: board.color_type,
        });
        await boardsRefetch();
      } catch (error) {
        showErrorToast('ボードの更新に失敗しました');
      }
    };

    const handleUpdateBoardColor = async (boardId: number, newColor: Color) => {
      try {
        const board = boards.find((b) => b.id === boardId);
        if (!board) return;

        await updateBoard({
          id: boardId,
          title: board.title,
          divide_status: board.divide_status,
          order_number: board.order_number,
          color_type: newColor,
        });
        await boardsRefetch();
      } catch (error) {
        showErrorToast('ボードのカラー更新に失敗しました');
      }
    };

    const handleDragEnd = async (result: DropResult) => {
      if (!result.destination) return;

      const sourceDroppableId = result.source.droppableId;
      const destinationDroppableId = result.destination.droppableId;
      if (destinationDroppableId === 'uncategorized') return;

      const profileId = Number(result.draggableId);
      const moved = (grouped[sourceDroppableId] || []).find((profile) => profile.id === profileId);
      if (!moved) return;

      const previousGrouped = grouped;
      const previousCounts = counts;

      const nextGrouped = { ...grouped };
      const sourceProfiles = [...(nextGrouped[sourceDroppableId] || [])];
      const removedIndex = sourceProfiles.findIndex((profile) => profile.id === profileId);
      if (removedIndex === -1) return;
      sourceProfiles.splice(removedIndex, 1);
      nextGrouped[sourceDroppableId] = sourceProfiles;

      const destinationProfiles =
        sourceDroppableId === destinationDroppableId
          ? sourceProfiles
          : [...(nextGrouped[destinationDroppableId] || [])];
      destinationProfiles.splice(result.destination.index, 0, moved);
      nextGrouped[destinationDroppableId] = destinationProfiles;
      setGrouped(nextGrouped);

      if (sourceDroppableId !== destinationDroppableId) {
        setCounts((prev) => ({
          ...prev,
          [sourceDroppableId]: Math.max((prev[sourceDroppableId] || 0) - 1, 0),
          [destinationDroppableId]: (prev[destinationDroppableId] || 0) + 1,
        }));
      }

      try {
        await moveProfile({
          candidate_profile_id: profileId,
          target_board_id: Number(destinationDroppableId.replace('board_', '')),
          order_number: result.destination.index,
        });
        // 蓄積済みのページは移動前のままなので、移動に関わった列は取り直す
        reloadBoards(
          [...new Set([sourceDroppableId, destinationDroppableId])]
            .filter((droppableId) => droppableId.startsWith('board_'))
            .map((droppableId) => Number(droppableId.replace('board_', '')))
        );
        if (sourceDroppableId === 'uncategorized') {
          uncategorizedRefetch({ resetPage: true });
        }
      } catch (error) {
        setGrouped(previousGrouped);
        setCounts(previousCounts);
        showErrorToast('プロファイルの移動に失敗しました');
      }
    };

    const showSkeleton = !hasLoaded;

    return (
      <div className="relative w-full overflow-hidden">
        <DragDropContext onDragEnd={handleDragEnd}>
          <div
            ref={scrollContainerRef}
            className="hidden-scrollbar flex h-[calc(100vh_-_166px)] flex-nowrap gap-3 overflow-x-auto px-6"
            data-testid="board-scroll-container"
          >
            {showSkeleton ? (
              Array.from({ length: 10 }).map((_, index) => <CandidatesBoardLoading key={index} />)
            ) : (
              <>
                {grouped.uncategorized && grouped.uncategorized.length > 0 && (
                  <CandidatesBoardColumn
                    colorType="gray"
                    title="未分類"
                    divideStatus="uncategorized"
                    profiles={grouped.uncategorized}
                    onProfileClick={setSelectedProfileId}
                    isDraggable={true}
                    totalCount={counts.uncategorized}
                    onLoadMore={handleLoadMore}
                    hasNextPage={uncategorizedHasNextPage}
                    isLoadingMore={uncategorizedIsFetching}
                  />
                )}

                {boards.map((b) => (
                  <CandidatesBoardColumn
                    key={b.id}
                    id={b.id}
                    colorType={b.color_type}
                    title={b.title}
                    divideStatus={`board_${b.id}`}
                    profiles={grouped[`board_${b.id}`] || []}
                    onProfileClick={setSelectedProfileId}
                    onTitleChange={(newTitle: string) => handleUpdateBoardTitle(b.id, newTitle)}
                    onColorChange={(color: Color) => handleUpdateBoardColor(b.id, color)}
                    onDelete={handleDeleteBoard}
                    isDraggable={true}
                    onLoadMore={handleLoadMore}
                    hasNextPage={boardStates[b.id]?.hasNextPage ?? false}
                    isLoadingMore={boardStates[b.id]?.isFetching ?? false}
                    totalCount={counts[`board_${b.id}`]}
                    boards={{ records: boards }}
                    boardsRefetch={boardsRefetch}
                  />
                ))}

                <button
                  type="button"
                  className="flex h-[52px] w-[272px] min-w-[272px] items-center gap-2 rounded border border-solid border-border bg-white px-4"
                  onClick={handleCreateBoard}
                >
                  <img src="/agent/candidates/icon-add-black.svg" width={24} height={24} alt="" />
                  <p className="text-base font-bold">新規ボードを作成</p>
                </button>
              </>
            )}
          </div>
        </DragDropContext>

        <CandidatesShowDrawer
          boards={{ records: boards }}
          isOpen={selectedProfileId !== null}
          onClose={() => setSelectedProfileId(null)}
          profileId={selectedProfileId}
          onBoardChanged={() => {
            boardsRefetch({ resetPages: true });
            uncategorizedRefetch({ resetPage: true });
          }}
        />
      </div>
    );
  }
);

CandidatesBoardScreen.displayName = 'CandidatesBoardScreen';

const DefaultCandidatesBoardScreen = CandidatesBoardScreen;
export default DefaultCandidatesBoardScreen;
